import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Compass, KeyRound } from 'lucide-react';
import { SPECIALTY_PAGES, MARKET_LINKS, SERVICE_AREAS_LINK, cityHeadline, keywordLinks, KEYWORD_COUNTRIES } from '../../data/seoLinks';
import { COUNTRIES } from '../../data/countryMarkets';

const slug = (to) => to.split('/').filter(Boolean).join('-');

export const HeadlineLink = ({ item, testid }) => (
  <li>
    <Link to={item.to} className="group block" data-testid={testid}>
      <span className="sub-link-hover inline-block relative text-xs font-semibold text-foreground/80 group-hover:text-primary transition-colors">{item.label}</span>
      <span className="block text-[11px] leading-snug text-muted-foreground mt-0.5">{item.headline}</span>
    </Link>
  </li>
);

const Col = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-bold uppercase tracking-widest mb-4 text-xs text-foreground">{title}</h3>
    <ul className="space-y-3">{items.map((it) => <HeadlineLink key={it.to + it.label} item={it} testid={`${testid}-${slug(it.to)}`} />)}</ul>
  </div>
);

// Content stays in the DOM when collapsed so search engines still crawl every link
const Collapse = ({ open, children, testid }) => (
  <motion.div initial={false} animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.35, ease: 'easeInOut' }} className="overflow-hidden" aria-hidden={!open} data-testid={testid}>
    {children}
  </motion.div>
);

const ToggleBar = ({ open, onClick, icon: Icon, title, headline, testid, small = false }) => (
  <button type="button" onClick={onClick} aria-expanded={open} className={`w-full flex items-center gap-4 text-left group ${small ? 'py-3' : 'py-5'}`} data-testid={testid}>
    <Icon size={small ? 16 : 20} className="text-primary shrink-0" />
    <span className="flex-1 min-w-0">
      <span className={`block font-black uppercase tracking-widest group-hover:text-primary transition-colors ${small ? 'text-xs' : 'text-sm'}`}>{title}</span>
      <span className="block text-xs text-muted-foreground mt-0.5">{headline}</span>
    </span>
    <ChevronDown size={18} className={`text-primary transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
  </button>
);

const AllKeywords = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('usa');
  return (
    <div className="border-t border-border mt-8" data-testid="footer-all-keywords">
      <ToggleBar small open={open} onClick={() => setOpen((o) => !o)} icon={KeyRound} title="All Keywords" headline="Every search term we target across the USA, Canada, UK and Australia, linked to its page." testid="footer-all-keywords-toggle" />
      <Collapse open={open} testid="footer-all-keywords-panel">
        <div className="flex flex-wrap gap-2 mb-6">
          {KEYWORD_COUNTRIES.map((k) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`text-xs font-bold uppercase tracking-widest px-4 py-2 border transition-colors ${tab === k ? 'bg-primary text-white border-primary' : 'border-border hover:border-primary hover:text-primary'}`} data-testid={`footer-keywords-tab-${k}`}>
              {COUNTRIES[k].name}
            </button>
          ))}
        </div>
        {KEYWORD_COUNTRIES.map((k) => (
          <ul key={k} className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 pb-6 ${tab === k ? '' : 'hidden'}`} data-testid={`footer-keywords-${k}`}>
            {keywordLinks(k).map((it, i) => <HeadlineLink key={it.label} item={{ ...it, label: it.label.charAt(0).toUpperCase() + it.label.slice(1) }} testid={`footer-keyword-${k}-${i}`} />)}
          </ul>
        ))}
      </Collapse>
    </div>
  );
};

export const FooterSeoLinks = () => {
  const [open, setOpen] = useState(false);
  const cityCols = ['canada', 'uk', 'australia'].map((c) => ({
    key: c,
    title: `${COUNTRIES[c].short} Cities`,
    items: COUNTRIES[c].cities.map((city) => ({ label: `Survey Drafting ${city.name}`, to: `/${c}/${city.slug}`, headline: cityHeadline(c, city.name) })),
  }));

  return (
    <div className="border-t border-border relative z-10" data-testid="footer-seo-links">
      <div className="container-custom">
        <ToggleBar open={open} onClick={() => setOpen((o) => !o)} icon={Compass} title="Explore All Services & Locations" headline="Specialty survey CAD services, global markets and city pages across four countries we serve." testid="footer-seo-toggle" />
        <Collapse open={open} testid="footer-seo-panel">
          <div className="pb-10 pt-2">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
              <Col title="Specialty Services" items={SPECIALTY_PAGES} testid="footer-specialty" />
              <Col title="Global Markets" items={[...MARKET_LINKS, SERVICE_AREAS_LINK]} testid="footer-markets" />
              {cityCols.map((c) => <Col key={c.key} title={c.title} items={c.items} testid={`footer-cities-${c.key}`} />)}
            </div>
            <AllKeywords />
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default FooterSeoLinks;
